import { useRef, useState, type DragEvent } from 'react';
import { Paperclip, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FileDropProps {
  /** Comma-separated list passed to the underlying input's `accept` attribute. */
  accept?: string;
  /** Current file. The parent owns state — drop / browse / clear bubble up here. */
  value: File | null;
  onChange: (file: File | null) => void;
  /** Marks the underlying input as required so the form's native validation
   *  catches an empty drop zone before submit. */
  required?: boolean;
  /** Short hint shown when no file is selected (e.g. "Transaction Detail"). */
  label: string;
  /** Optional sub-hint shown beneath the main label (e.g. file-format spec). */
  hint?: string;
  /** Hard cap surfaced inline. Doesn't enforce — server still validates. */
  maxBytes?: number;
}

/** Drag-and-drop file zone backed by a hidden `<input type="file">`.
 *
 * Why not just a native `<input>` with `accept`? Native inputs are tiny
 * targets on touchpads + don't surface the selected filename clearly.
 * Drop zones make uploading multi-MB exports feel less fiddly.
 *
 * The input itself is kept in the DOM (hidden) so that:
 *   - Browse-click works via `onClick` triggering `inputRef.current.click()`.
 *   - The native required validation message anchors to the input on submit.
 *   - Form reset clears it without bespoke wiring.
 */
export function FileDrop({
  accept,
  value,
  onChange,
  required = false,
  label,
  hint,
  maxBytes,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function openPicker() {
    inputRef.current?.click();
  }

  function clear() {
    onChange(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    // preventDefault so the browser doesn't open the file as a new tab on drop
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file) onChange(file);
  }

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-6 text-center transition-colors',
        dragOver
          ? 'border-brand-royal bg-brand-royal/5'
          : 'border-input bg-transparent hover:border-brand-royal/60',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        required={required && value === null}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        className="sr-only"
      />
      {value ? (
        <div className="flex w-full items-center justify-between gap-3 text-left">
          <div className="flex min-w-0 items-center gap-2">
            <Paperclip size={16} strokeWidth={1.75} className="shrink-0 text-brand-slate" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-brand-navy">{value.name}</p>
              <p className="text-xs text-brand-slate">{formatBytes(value.size)}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={openPicker}>
              Replace
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clear}
              aria-label="Remove file"
            >
              <X size={14} strokeWidth={1.75} />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex flex-col items-center gap-1 rounded-sm text-sm text-brand-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal focus-visible:ring-offset-2"
        >
          <Upload size={20} strokeWidth={1.75} className="text-brand-slate/70" />
          <span className="font-medium text-brand-navy">{label}</span>
          <span className="text-xs">
            Drop a file here or <span className="text-brand-royal underline">browse</span>
          </span>
          {hint && <span className="text-xs">{hint}</span>}
          {maxBytes && <span className="text-xs">Max {formatBytes(maxBytes)}</span>}
        </button>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
